import { memo, useCallback, useEffect, useState } from 'react'
import { ArrowRightLeft, Radio, Scissors } from 'lucide-react'
import { playoutApi } from '../../lib/api.js'
import { getServerNowMs } from '../../hooks/useServerClock.js'
import { usePlayoutState } from '../../store/index.js'
import { hasNoSdiSignal } from '../../lib/liveSignal.js'
import type { ScheduleItem } from '../../types/index.js'
import { LiveSourceSwitchDialog } from './LiveSourceSwitchDialog.js'

/**
 * Bandeau de pilotage du direct, juste au-dessus des quatre boutons principaux.
 *
 * Un seul emplacement porte tous les états, pour que l'opérateur sache toujours
 * où regarder :
 *   • départ manuel en attente  → « Passer au direct » ;
 *   • fin manuelle              → « Fin du direct » ;
 *   • fin automatique, à moins de deux minutes du top → « Continuer le direct »,
 *     qui bascule en fin manuelle et fait donc apparaître le bouton ci-dessus.
 *
 * Un direct est un bloc : on peut le quitter et y revenir à tout moment.
 * « Décrocher — infoservice » part tout de suite sur l'infoservice et son
 * habillage, sans durée annoncée — c'est le pendant du bouton Interlude, et on
 * revient par « Reprendre le direct ». « Décrocher (modèle) » pose le décrochage
 * préfait de la journée, « Décrocher… » ouvre la composition libre.
 *
 * Le mode se change à tout moment, dans les deux sens, y compris loin de la fin :
 * la puce AUTO/MANUEL reste cliquable tant que le direct est à l'antenne. Rien
 * n'est bloquant, et le même réglage existe dans les paramètres de l'item de
 * grille.
 */

/** Avance à laquelle on propose de poursuivre un direct en fin automatique. */
const ENDING_SOON_SEC = 120

interface LiveControlBannerProps {
  channelId: string
  onAirItem: ScheduleItem
  /** Direct manuel dont le top de départ n'a pas encore été donné. */
  awaitingStart: boolean
  disabled: boolean
  goLivePending: boolean
  endLivePending: boolean
  onGoLive: () => void
  onEndLive: () => void
  /** Ouvre la composition du décrochage. */
  onBreak: () => void
  /**
   * Décrochage immédiat sur l'infoservice, sans rien composer : le pendant du
   * bouton Interlude, avec l'habillage d'infoservice et un retour à la demande.
   */
  onBreakInfoservice: () => void
  /** Pose le décrochage préfait du jour, sans autre question. */
  onBreakWithTemplate?: () => void
  breakPending: boolean
  /** Vrai quand un décrochage préfait existe pour la journée d'antenne. */
  hasTemplate: boolean
}

function formatCountdown(sec: number): string {
  const safe = Math.max(0, Math.floor(sec))
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`
}

export const LiveControlBanner = memo(function LiveControlBanner({
  channelId,
  onAirItem,
  awaitingStart,
  disabled,
  goLivePending,
  endLivePending,
  onGoLive,
  onEndLive,
  onBreak,
  onBreakInfoservice,
  onBreakWithTemplate,
  breakPending,
  hasTemplate,
}: LiveControlBannerProps) {
  const [now, setNow] = useState(getServerNowMs)
  const [modePending, setModePending] = useState(false)
  const [error, setError] = useState('')
  const [switchOpen, setSwitchOpen] = useState(false)

  // Le compte à rebours doit être juste à la seconde : c'est lui qui décide
  // quand la proposition de poursuivre apparaît.
  useEffect(() => {
    const timer = setInterval(() => setNow(getServerNowMs()), 1000)
    return () => clearInterval(timer)
  }, [])

  const isManual = onAirItem.liveEndMode === 'MANUAL'

  // Sonde SDI, informative : `NO_SIGNAL` est le seul état certain, l'inconnu
  // ne doit jamais allumer l'alerte (cf. `lib/liveSignal.ts`).
  const playoutState = usePlayoutState()
  const noSignal = hasNoSdiSignal(playoutState?.casparStatus)

  const setMode = useCallback(async (mode: 'AUTO' | 'MANUAL') => {
    if (modePending) return
    setModePending(true)
    setError('')
    try {
      await playoutApi.setLiveEndMode(channelId, onAirItem.id, mode)
    } catch (cause: any) {
      setError(cause.message ?? 'Bascule impossible')
    } finally {
      setModePending(false)
    }
  }, [channelId, modePending, onAirItem.id])

  const handleContinue = useCallback(() => { void setMode('MANUAL') }, [setMode])
  const handleToggleMode = useCallback(() => { void setMode(isManual ? 'AUTO' : 'MANUAL') }, [isManual, setMode])

  // Flux du direct et bascule vers le flux de secours : présents dans les deux
  // états du bandeau, la bascule ne dépend ni du mode de fin ni du départ.
  const sourceRow = (
    <div className="monitor-manual-live__source">
      <span className="monitor-manual-live__source-name">
        Flux : <strong>{onAirItem.liveSource?.name ?? 'inconnu'}</strong>
      </span>
      <button
        type="button"
        className="tp-button tp-button--ghost monitor-manual-live__break-variant"
        disabled={disabled}
        onClick={() => setSwitchOpen(true)}
        title="Basculer le direct sur un autre flux Internet (flux de secours)"
      >
        <ArrowRightLeft size={13} aria-hidden="true" />
        changer de flux…
      </button>
      {switchOpen && (
        <LiveSourceSwitchDialog channelId={channelId} item={onAirItem} onClose={() => setSwitchOpen(false)} />
      )}
    </div>
  )

  const endsInSec = (new Date(onAirItem.endTime).getTime() - now) / 1000
  const endingSoon = !isManual && endsInSec <= ENDING_SOON_SEC

  // ── Départ manuel en attente : la boucle tient l'antenne ────────────────────
  if (awaitingStart) {
    return (
      <div className="monitor-manual-live">
        <div className="monitor-manual-live__copy">
          <strong>Direct manuel en attente</strong>
          <span>La boucle d'attente est à l'antenne.</span>
          {noSignal && (
            <span className="monitor-manual-live__no-signal" role="alert">
              ⚠ Aucun signal SDI — passer au direct mettrait du noir à l'antenne.
            </span>
          )}
        </div>
        {sourceRow}
        <button
          type="button"
          className="tp-button tp-button--critical monitor-manual-live__button"
          disabled={disabled || goLivePending}
          onClick={onGoLive}
        >
          <Radio size={16} aria-hidden="true" />
          {goLivePending ? 'Passage…' : 'Passer au direct'}
        </button>
      </div>
    )
  }

  return (
    <div className="monitor-manual-live" data-ending={endingSoon || undefined}>
      <div className="monitor-manual-live__copy">
        <strong>
          {isManual
            ? 'Direct en cours — fin manuelle'
            : endingSoon
              ? `Direct coupé dans ${formatCountdown(endsInSec)}`
              : 'Direct en cours — fin automatique'}
        </strong>
        <span>
          {isManual
            ? 'Coupez le direct quand vous le décidez.'
            : endingSoon
              ? 'Poursuivez le direct pour reprendre la main sur la coupure.'
              : `Coupure prévue à l'heure de grille, dans ${formatCountdown(endsInSec)}.`}
        </span>
      </div>

      {sourceRow}

      {isManual ? (
        <button
          type="button"
          className="tp-button tp-button--critical monitor-manual-live__button"
          disabled={disabled || endLivePending}
          onClick={onEndLive}
        >
          <Radio size={16} aria-hidden="true" />
          {endLivePending ? 'Coupure…' : 'Fin du direct'}
        </button>
      ) : endingSoon ? (
        <button
          type="button"
          className="tp-button tp-button--critical monitor-manual-live__button"
          disabled={disabled || modePending}
          onClick={handleContinue}
        >
          <Radio size={16} aria-hidden="true" />
          {modePending ? 'Bascule…' : 'Continuer le direct'}
        </button>
      ) : null}

      {/* Décrochage : une seule action évidente — quitter l'antenne sur
          l'infoservice — et deux variantes en retrait. Trois boutons de même
          poids ne disaient pas lequel presser, ce qui est le pire défaut
          possible sur un bandeau qu'on utilise en direct. */}
      <div className="monitor-manual-live__breaks">
        <button
          type="button"
          className="tp-button tp-button--outline-warning monitor-manual-live__break"
          disabled={disabled || breakPending}
          onClick={onBreakInfoservice}
          title="Quitte l'antenne tout de suite sur l'infoservice et son habillage — vous reprenez le direct quand vous voulez"
        >
          <Scissors size={15} aria-hidden="true" />
          {breakPending ? 'Décrochage…' : 'Décrocher — infoservice'}
        </button>

        <div className="monitor-manual-live__break-variants">
          {hasTemplate && onBreakWithTemplate && (
            <button
              type="button"
              className="tp-button tp-button--ghost monitor-manual-live__break-variant"
              disabled={disabled || breakPending}
              onClick={onBreakWithTemplate}
              title="Pose le décrochage préparé pour cette journée d'antenne"
            >
              modèle du jour
            </button>
          )}
          <button
            type="button"
            className="tp-button tp-button--ghost monitor-manual-live__break-variant"
            disabled={disabled || breakPending}
            onClick={onBreak}
            title="Choisir le contenu et la durée du décrochage"
          >
            composer…
          </button>
        </div>
      </div>

      {/* La bascule reste offerte en permanence : on doit pouvoir rendre la main
          aussi facilement qu'on l'a prise, même à dix minutes de la fin. */}
      <button
        type="button"
        className="monitor-manual-live__mode"
        disabled={disabled || modePending}
        onClick={handleToggleMode}
        aria-label={`Fin ${isManual ? 'manuelle' : 'automatique'} — basculer en ${isManual ? 'automatique' : 'manuel'}`}
      >
        <span className="monitor-manual-live__mode-value">{isManual ? 'MANUEL' : 'AUTO'}</span>
        <span className="monitor-manual-live__mode-action">
          {modePending ? '…' : `passer en ${isManual ? 'automatique' : 'manuel'}`}
        </span>
      </button>

      {error && <div className="monitor-manual-live__error" role="alert">⚠ {error}</div>}
    </div>
  )
})
